/**
 * Waitlist API.
 *
 * Joining goes through the `signup` edge function (`action: "waitlist"`), not
 * straight to the table: the row has to be written by something that can check
 * the Turnstile token and meter the caller's IP, and neither is possible from the
 * browser. `waitlist` has no INSERT policy at all — see migration 053.
 *
 * Reading and working the list is ordinary PostgREST, gated by RLS to admins and
 * moderators.
 */

import supabase from "../config/supabase";
import { invokeFunction } from "./functionErrors";

const waitlistApi = {
  /**
   * Adds someone to the list.
   *
   * `already` comes back true when that mobile was on it already. Still a
   * success, because it is — and saying otherwise would tell anyone who asked
   * whether a given number is on the list.
   */
  async join({ first_name, mobile, location, consent, turnstile_token }) {
    return invokeFunction(
      supabase,
      "signup",
      {
        body: {
          action: "waitlist",
          first_name,
          mobile,
          location,
          consent,
          turnstile_token,
        },
      },
      "Could not add you to the waitlist just now. Please try again.",
    );
  },

  /** The list, newest first. Staff only. */
  async list({ status } = {}) {
    let query = supabase
      .from("waitlist")
      .select("*")
      .order("created_at", { ascending: false });

    if (status) query = query.eq("status", status);

    const { data, error } = await query;
    if (error) return { success: false, error: error.message };
    return { success: true, data: data || [] };
  },

  /** Marks where someone is up to. Setting 'invited' stamps when. */
  async setStatus(id, status) {
    const patch = { status };
    if (status === "invited") patch.invited_at = new Date().toISOString();

    const { error } = await supabase.from("waitlist").update(patch).eq("id", id);
    if (error) return { success: false, error: error.message };
    return { success: true };
  },

  /** Deletes the row for good. Admins only, per 053. */
  async remove(id) {
    const { error } = await supabase.from("waitlist").delete().eq("id", id);
    if (error) return { success: false, error: error.message };
    return { success: true };
  },

  async setNotes(id, notes) {
    const { error } = await supabase
      .from("waitlist")
      .update({ notes: notes || null })
      .eq("id", id);
    if (error) return { success: false, error: error.message };
    return { success: true };
  },
};

export default waitlistApi;
